import { Middleware, Dispatch } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import _ from 'lodash';
import {
  ActionMetaOrUndefined,
  DashboardInfo,
  DeleteDashboardPath,
  StorageDashboardPath,
  StorageDashboardsPath,
  BlockConfiguration,
  BlockGroupState,
  Layouts,
  StorageBlocksPath,
} from '@tradingblock/types';
import { DataState } from '../../state';
import { dashboardIdSelector, blocksSelectors, layoutSelectors } from '../../dataSelectors';
import {
  DashboardActions,
  RootAction,
  BlockActions,
  LayoutActions,
  BlockGroupActions,
  SharedBlockGroupActions,
  UIActions,
} from '../../actions';
import { UiApiClient } from '../../../../context/Storage';

export const handleDashboardDeletion = async (
  state: DataState,
  dispatch: Dispatch<RootAction>,
  payload: {
    dashboardId: string;
    dashboardData: Partial<DashboardInfo>;
  }
) => {
  const provider = UiApiClient(state, dispatch);
  const { dashboardId } = payload;
  if (provider) {
    await provider.delete(DeleteDashboardPath(dashboardId));
  }
};

export const handleCreateDashboard = async (
  state: DataState,
  dispatch: Dispatch<RootAction>,
  action: ActionType<typeof DashboardActions.addEmptyDashboard>
) => {
  const provider = UiApiClient(state, dispatch);
  const { dashboard, setAsActive } = action.payload;

  // Signal start of dashboard transition
  dispatch(UIActions.beginDashboardTransition());

  if (provider) {
    await provider.save(StorageDashboardPath, dashboard);

    if (setAsActive) {
      dispatch(BlockActions.clearBlocks());
      dispatch(DashboardActions.setDashboard({ dashboard }));
    }
  }
};

export const tryLoadDashboards = async (state: DataState, dispatch: Dispatch<RootAction>) => {
  const provider = UiApiClient(state, dispatch);
  if (provider) {
    const values = await provider.get<DashboardInfo[]>(StorageDashboardsPath);
    dispatch(DashboardActions.receiveDashboards(values || []));
  }
};

interface BlockData {
  blocks?: BlockConfiguration[];
  groups: BlockGroupState[];
  layouts?: Layouts;
}

const persistBlocks = async (state: DataState, dispatch: Dispatch<RootAction>, action: RootAction) => {
  // Don't persist blocks if we're transitioning
  if (state.ui.dashboard.isTransitioning) {
    return;
  }

  const dashboardId = dashboardIdSelector(state);
  const blocks = blocksSelectors.blocks(state);
  const groups = blocksSelectors.blockGroups(state);
  const layouts = layoutSelectors.layouts(state);
  const provider = UiApiClient(state, dispatch);
  if (provider) {
    try {
      if (dashboardId === undefined) {
        console.warn('trying to save blocks with an undefined dashboardId');
        return;
      }
      const blockData: BlockData = {
        groups,
      };

      if (action.type === getType(LayoutActions.setLayouts) && layouts !== undefined) {
        blockData.layouts = layouts;
      }

      if (action.type !== getType(LayoutActions.setLayouts)) {
        // need to ensure that we do not save block data with mismatched symbols
        // if the block is a member of a group, we need to ensure that the symbol matches the group symbol
        // if it does not, we need to update the block symbol to match the group symbol
        blockData.blocks = blocks.map((b: any) => {
          const group = groups.find((g: { groupId: any }) => g.groupId === b.groupId);
          if (group && group.symbol !== b.symbol) {
            return {
              ...b,
              symbol: group.symbol,
              data: {
                ...b.data,
                symbol: group.symbol,
              },
            };
          }
          return b;
        });

        blockData.blocks = _.map(blocks, b => ({ ...b, subscriptions: undefined }));
      }

      const res = await provider
        .save<BlockData>(StorageBlocksPath(dashboardId), blockData)
        .then(res => (_.isArray(res) ? res : []));
    } catch (ex) {
      console.error('persistBlocks', ex);
      return Promise.resolve([]);
    }
  }
};

const tryPersistBlocks = async (
  state: DataState,
  dispatch: Dispatch<RootAction>,
  actionMeta: ActionMetaOrUndefined,
  action: RootAction
) => {
  if (actionMeta && actionMeta.persist === false) {
    return;
  }

  // Get the current dashboard ID
  const dashboardId = dashboardIdSelector(state);

  // Check if we're in a dashboard transition
  const isTransitioning = state.ui.dashboard.isTransitioning;

  // Don't persist if we're transitioning between dashboards
  if (isTransitioning || !dashboardId) {
    return;
  }

  persistBlocks(state, dispatch, action);
  return;
};

export const DashboardMiddleware: Middleware<Dispatch<RootAction>, DataState, Dispatch<RootAction>> = ({
  dispatch,
  getState,
}) => (next: Dispatch<RootAction>) => async (action: RootAction) => {
  try {
    const result = next(action);
    const nextState = getState();
    switch (action.type) {
      case getType(DashboardActions.setDashboard): {
        dispatch(UIActions.beginDashboardTransition());
        return result;
      }
      case getType(BlockActions.addBlock):
      case getType(BlockActions.addOrUpdateBlock):
      case getType(BlockActions.removeBlock):
      case getType(BlockActions.updateBlock):
      case getType(BlockActions.linkBlock):
      case getType(BlockActions.unlinkBlock):
      case getType(LayoutActions.setLayouts):
      case getType(LayoutActions.setAllLayouts):
      case getType(BlockGroupActions.setGroupSymbol):
      case getType(SharedBlockGroupActions.SetSymbolAndReset): {
        if (nextState.blocks) {
          tryPersistBlocks(nextState, dispatch, _.get(action, 'meta', undefined), action);
        }
        break;
      }
      case getType(DashboardActions.updateDashboard): {
        if (action.payload.dashboardData.isDeleted) {
          handleDashboardDeletion(nextState, dispatch, action.payload);
        }
        break;
      }
      case getType(DashboardActions.addEmptyDashboard): {
        handleCreateDashboard(nextState, dispatch, action);
        break;
      }
      case getType(DashboardActions.requestDashboards): {
        tryLoadDashboards(nextState, dispatch);
        break;
      }
    }
    return result;
  } catch (err) {
    console.error('DashboardMiddleware :: Caught an exception for action ', action, err);
  }
};
